/**
 * flash.filters.* as plain data classes. Filters are rendered by the (browser) renderer; the
 * headless runtime only stores them. `BitmapData.applyFilter` implements ColorMatrixFilter and
 * BlurFilter in software (other filters: UNVERIFIED approximation, see BitmapData.applyFilter).
 */
import type { FilterDef } from '../../assets/manifest';

export const BitmapFilterQuality = { LOW: 1, MEDIUM: 2, HIGH: 3 } as const;
export const BitmapFilterType = { INNER: 'inner', OUTER: 'outer', FULL: 'full' } as const;

export class BitmapFilter {
  clone(): BitmapFilter {
    return new BitmapFilter();
  }
}

export class BlurFilter extends BitmapFilter {
  blurX: number;
  blurY: number;
  quality: number;
  constructor(blurX: number = 4, blurY: number = 4, quality: number = 1) {
    super();
    this.blurX = +blurX;
    this.blurY = +blurY;
    this.quality = quality | 0;
  }
  clone(): BlurFilter {
    return new BlurFilter(this.blurX, this.blurY, this.quality);
  }
}

export class GlowFilter extends BitmapFilter {
  color: number;
  alpha: number;
  blurX: number;
  blurY: number;
  strength: number;
  quality: number;
  inner: boolean;
  knockout: boolean;
  constructor(color: number = 0xff0000, alpha: number = 1, blurX: number = 6, blurY: number = 6, strength: number = 2, quality: number = 1, inner: boolean = false, knockout: boolean = false) {
    super();
    this.color = color >>> 0;
    this.alpha = +alpha;
    this.blurX = +blurX;
    this.blurY = +blurY;
    this.strength = +strength;
    this.quality = quality | 0;
    this.inner = !!inner;
    this.knockout = !!knockout;
  }
  clone(): GlowFilter {
    return new GlowFilter(this.color, this.alpha, this.blurX, this.blurY, this.strength, this.quality, this.inner, this.knockout);
  }
}

export class DropShadowFilter extends BitmapFilter {
  [key: string]: any;
  distance: number;
  angle: number;
  color: number;
  alpha: number;
  blurX: number;
  blurY: number;
  strength: number;
  quality: number;
  inner: boolean;
  knockout: boolean;
  hideObject: boolean;
  constructor(
    distance: number = 4,
    angle: number = 45,
    color: number = 0,
    alpha: number = 1,
    blurX: number = 4,
    blurY: number = 4,
    strength: number = 1,
    quality: number = 1,
    inner: boolean = false,
    knockout: boolean = false,
    hideObject: boolean = false,
  ) {
    super();
    this.distance = +distance;
    this.angle = +angle;
    this.color = color >>> 0;
    this.alpha = +alpha;
    this.blurX = +blurX;
    this.blurY = +blurY;
    this.strength = +strength;
    this.quality = quality | 0;
    this.inner = !!inner;
    this.knockout = !!knockout;
    this.hideObject = !!hideObject;
  }
  clone(): DropShadowFilter {
    return new DropShadowFilter(this.distance, this.angle, this.color, this.alpha, this.blurX, this.blurY, this.strength, this.quality, this.inner, this.knockout, this.hideObject);
  }
}

export class BevelFilter extends BitmapFilter {
  distance: number;
  angle: number;
  highlightColor: number;
  highlightAlpha: number;
  shadowColor: number;
  shadowAlpha: number;
  blurX: number;
  blurY: number;
  strength: number;
  quality: number;
  type: string;
  knockout: boolean;
  constructor(
    distance: number = 4,
    angle: number = 45,
    highlightColor: number = 0xffffff,
    highlightAlpha: number = 1,
    shadowColor: number = 0,
    shadowAlpha: number = 1,
    blurX: number = 4,
    blurY: number = 4,
    strength: number = 1,
    quality: number = 1,
    type: string = 'inner',
    knockout: boolean = false,
  ) {
    super();
    this.distance = distance;
    this.angle = angle;
    this.highlightColor = highlightColor >>> 0;
    this.highlightAlpha = highlightAlpha;
    this.shadowColor = shadowColor >>> 0;
    this.shadowAlpha = shadowAlpha;
    this.blurX = blurX;
    this.blurY = blurY;
    this.strength = strength;
    this.quality = quality | 0;
    this.type = type;
    this.knockout = knockout;
  }
  clone(): BevelFilter {
    return new BevelFilter(this.distance, this.angle, this.highlightColor, this.highlightAlpha, this.shadowColor, this.shadowAlpha, this.blurX, this.blurY, this.strength, this.quality, this.type, this.knockout);
  }
}

/** 4x5 color matrix; Flash copies the array and pads/truncates to 20 entries. */
export class ColorMatrixFilter extends BitmapFilter {
  /** @internal */ $matrix: number[];
  constructor(matrix?: number[] | null) {
    super();
    this.$matrix = ColorMatrixFilter.normalize(matrix ?? [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0]);
  }
  static normalize(m: number[]): number[] {
    const out = new Array<number>(20);
    for (let i = 0; i < 20; i++) {
      const v = Number(m[i]);
      out[i] = Number.isFinite(v) ? v : 0;
    }
    return out;
  }
  /** Getter returns a copy (Flash semantics: mutating the returned array has no effect). */
  get matrix(): number[] {
    return this.$matrix.slice();
  }
  set matrix(m: number[]) {
    this.$matrix = ColorMatrixFilter.normalize(m ?? []);
  }
  clone(): ColorMatrixFilter {
    return new ColorMatrixFilter(this.$matrix);
  }
}

/** Filters produced by the manifest for unsupported types (kept for the renderer). */
export class RawFilter extends BitmapFilter {
  constructor(readonly def: FilterDef) {
    super();
  }
  clone(): RawFilter {
    return new RawFilter(this.def);
  }
}

/** Convert a manifest FilterDef to a runtime filter object. */
export function filterFromDef(def: FilterDef): BitmapFilter {
  switch (def.type) {
    case 'blur':
      return new BlurFilter(def.blurX, def.blurY, def.quality);
    case 'glow':
      return new GlowFilter(def.color & 0xffffff, def.alpha, def.blurX, def.blurY, def.strength, def.quality, def.inner, def.knockout);
    case 'dropShadow':
      return new DropShadowFilter(def.distance, def.angle, def.color & 0xffffff, def.alpha, def.blurX, def.blurY, def.strength, def.quality, def.inner, def.knockout, def.hideObject);
    case 'bevel':
      return new BevelFilter(def.distance, def.angle, def.highlightColor & 0xffffff, def.highlightAlpha, def.shadowColor & 0xffffff, def.shadowAlpha, def.blurX, def.blurY, def.strength, def.quality, def.inner ? 'inner' : def.onTop ? 'full' : 'outer', def.knockout);
    case 'colorMatrix':
      return new ColorMatrixFilter(def.matrix);
    default:
      return new RawFilter(def);
  }
}
